/**
 * PlanPicker — replaces TemplateSwitcher in the Practice Hub eyebrow row.
 *
 * Shows the active plan label with a dropdown listing App Plans and (in PR 4)
 * a "Manage plans" link that opens the full PlanLibrary modal.
 */

import { type ReactNode, useState, useRef, useEffect, useCallback } from "react";
import { APP_PLANS, type SavedPlan } from "@music-app/learning-system";

interface PlanPickerProps {
  activePlan: SavedPlan;
  mode: "standard" | "intervention";
  userPlans: SavedPlan[];
  onSelect: (id: string) => void;
  onManagePlans?: () => void;
}

export function PlanPicker({
  activePlan,
  mode,
  userPlans,
  onSelect,
  onManagePlans,
}: PlanPickerProps): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  if (mode === "intervention") {
    return (
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-warning">
        focus session
      </p>
    );
  }

  return (
    <div ref={ref} className="relative" onKeyDown={handleKeyDown}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:opacity-75 transition-opacity"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Active plan: ${activePlan.name}. Click to change.`}
      >
        {activePlan.name} plan ▾
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-20 bg-paper border border-ink/15 min-w-[240px]"
          role="listbox"
          aria-label="Plan"
        >
          {userPlans.length > 0 && (
            <>
              <p className="px-3 pt-2.5 pb-1 font-mono text-[9px] uppercase tracking-widest text-inkFaded">
                My Plans
              </p>
              {userPlans.map((plan) => (
                <PlanOption
                  key={plan.id}
                  plan={plan}
                  active={activePlan.id === plan.id}
                  onSelect={(id) => { onSelect(id); setOpen(false); }}
                />
              ))}
            </>
          )}

          <p className="px-3 pt-2.5 pb-1 font-mono text-[9px] uppercase tracking-widest text-inkFaded">
            App Plans
          </p>
          {APP_PLANS.map((plan) => (
            <PlanOption
              key={plan.id}
              plan={plan}
              active={activePlan.id === plan.id}
              onSelect={(id) => { onSelect(id); setOpen(false); }}
            />
          ))}

          {onManagePlans && (
            <button
              type="button"
              onClick={() => { onManagePlans(); setOpen(false); }}
              className="block w-full text-left px-3 py-2.5 font-mono text-[10px] text-accent hover:bg-paperWarm transition-colors border-t border-ink/10"
            >
              + Manage plans →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PlanOption({
  plan,
  active,
  onSelect,
}: {
  plan: SavedPlan;
  active: boolean;
  onSelect: (id: string) => void;
}): ReactNode {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={() => onSelect(plan.id)}
      className={`block w-full text-left px-3 py-2.5 border-b border-ink/10 last:border-b-0 hover:bg-paperWarm transition-colors ${active ? "bg-paperWarm" : ""}`}
    >
      <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${active ? "text-accent" : "text-ink"}`}>
        {plan.name}
      </span>
    </button>
  );
}
